import { useZero } from "@rocicorp/zero/react";
import { useCallback, useMemo } from "react";
import { sharedMutators } from "../../shared/mutators";
import type { ShareableScopeKind } from "../../shared/rbac/permissions";
import {
  type GrantEntry,
  grantHasPermission,
  rolesForScope,
  SCOPE_MANAGE_GRANTS_PERMISSION,
} from "../../shared/rbac/roles";
import { customRolesForScope, grantForMutation } from "../lib/grantAssembly";
import { useHasAccess } from "./useHasAccess";
import { useManageAccessGuard } from "./useManageAccessGuard";

type RawGrant = {
  userGroupId?: string | null;
  userId?: string | null;
  serviceAccountId?: string | null;
  builtinRoleId?: string | null;
  customRoleId?: string | null;
};

type CustomRole = {
  id: string;
  name: string;
  scopeKind: string;
  rolePermissions: ReadonlyArray<{ permission: string }>;
};

type PersistedParams<
  G extends { id: string },
  U extends { id: string; deactivated?: boolean | null },
> = BaseParams<G, U> & {
  scopeId: string;
  rawGrants: readonly RawGrant[] | undefined;
  viewerId: string | undefined;
  resourceLabel: string;
  guardCommit?: (
    grants: GrantEntry[],
    proceed: (grants: GrantEntry[]) => void
  ) => void;
};

type LocalParams<
  G extends { id: string },
  U extends { id: string; deactivated?: boolean | null },
> = BaseParams<G, U> & {
  grants: GrantEntry[];
  onCommit: (grants: GrantEntry[]) => void;
};

type BaseParams<
  G extends { id: string },
  U extends { id: string; deactivated?: boolean | null },
> = {
  scopeKind: ShareableScopeKind;
  allGroups: readonly G[];
  allUsers: readonly U[];
  customRoles: readonly CustomRole[];
};

type Params<
  G extends { id: string },
  U extends { id: string; deactivated?: boolean | null },
> = PersistedParams<G, U> | LocalParams<G, U>;

export function useScopeGrants<
  G extends {
    id: string;
    roleGrantsAsScope?: readonly { userId?: string | null }[];
  },
  U extends { id: string; deactivated?: boolean | null },
>(params: Params<G, U>) {
  const z = useZero();
  const { scopeKind, allGroups, allUsers, customRoles } = params;
  const managePermission = SCOPE_MANAGE_GRANTS_PERMISSION[scopeKind];

  const persisted = "scopeId" in params;
  const scopeId = persisted ? params.scopeId : undefined;
  const rawGrants = persisted ? params.rawGrants : undefined;
  const viewerId = persisted ? params.viewerId : undefined;
  const resourceLabel = persisted ? params.resourceLabel : "";
  const guardCommit = persisted ? params.guardCommit : undefined;
  const localGrants = persisted ? undefined : params.grants;
  const onCommit = persisted ? undefined : params.onCommit;

  const scopeCustomRoles = useMemo(
    () => customRolesForScope(customRoles, scopeKind),
    [customRoles, scopeKind]
  );

  const roleOptions = useMemo(
    () => rolesForScope(scopeKind, scopeCustomRoles),
    [scopeKind, scopeCustomRoles]
  );

  const allGrants: GrantEntry[] = useMemo(() => {
    if (localGrants) return localGrants;
    if (!rawGrants) return [];
    return rawGrants
      .filter((g) => g.userGroupId || g.userId || g.serviceAccountId)
      .map((g) => ({
        userGroupId: g.userGroupId ?? undefined,
        userId: g.userId ?? undefined,
        serviceAccountId: g.serviceAccountId ?? undefined,
        builtinRoleId: g.builtinRoleId ?? undefined,
        customRoleId: g.customRoleId ?? undefined,
      }));
  }, [localGrants, rawGrants]);

  const assignedGroupIds = useMemo(
    () =>
      new Set(
        allGrants
          .map((g) => g.userGroupId)
          .filter((id): id is string => id != null)
      ),
    [allGrants]
  );

  const assignedUserIds = useMemo(
    () =>
      new Set(
        allGrants.map((g) => g.userId).filter((id): id is string => id != null)
      ),
    [allGrants]
  );

  const assignedGroups = useMemo(
    () => allGroups.filter((g) => assignedGroupIds.has(g.id)),
    [allGroups, assignedGroupIds]
  );

  const assignedUsers = useMemo(
    () => allUsers.filter((u) => assignedUserIds.has(u.id) && !u.deactivated),
    [allUsers, assignedUserIds]
  );

  const customRoleMap = useMemo(
    () => new Map(scopeCustomRoles.map((r) => [r.id, r])),
    [scopeCustomRoles]
  );

  const isManagerGrant = useCallback(
    (g: GrantEntry) =>
      grantHasPermission(
        {
          builtinRoleId: g.builtinRoleId ?? null,
          customRole: g.customRoleId
            ? (customRoleMap.get(g.customRoleId) ?? null)
            : null,
        },
        managePermission
      ),
    [customRoleMap, managePermission]
  );

  const viewerGroupIds = useMemo(() => {
    const ids = new Set<string>();
    if (!viewerId) return ids;
    for (const group of allGroups) {
      if (group.roleGrantsAsScope?.some((r) => r.userId === viewerId))
        ids.add(group.id);
    }
    return ids;
  }, [allGroups, viewerId]);

  const viewerIsTenantAdmin = useHasAccess("allow_all");

  const viewerRetainsManage = useCallback(
    (grants: GrantEntry[]) => {
      if (!viewerId || viewerIsTenantAdmin) return true;
      return grants.some(
        (g) =>
          isManagerGrant(g) &&
          ((g.userId != null && g.userId === viewerId) ||
            (g.userGroupId != null && viewerGroupIds.has(g.userGroupId)))
      );
    },
    [viewerId, viewerIsTenantAdmin, viewerGroupIds, isManagerGrant]
  );

  const doCommit = useCallback(
    (nextGrants: GrantEntry[]) => {
      if (onCommit) {
        onCommit(nextGrants);
        return;
      }
      const proceed = (grants: GrantEntry[]) => {
        z.mutate(
          sharedMutators.setScopeRoleGrants({
            scopeId: scopeId!,
            scopeKind,
            grants: grants.map(grantForMutation),
          })
        );
      };
      if (guardCommit) guardCommit(nextGrants, proceed);
      else proceed(nextGrants);
    },
    [z, scopeId, scopeKind, onCommit, guardCommit]
  );

  const { commitGrants, confirmModal: managerConfirmModal } =
    useManageAccessGuard({
      allGrants,
      doCommit,
      isManagerGrant,
      viewerRetainsManage,
      resourceLabel,
      enabled: persisted,
    });

  const removeGroup = useCallback(
    (groupId: string) => {
      commitGrants(allGrants.filter((g) => g.userGroupId !== groupId));
    },
    [allGrants, commitGrants]
  );

  const removeUser = useCallback(
    (userId: string) => {
      commitGrants(allGrants.filter((g) => g.userId !== userId));
    },
    [allGrants, commitGrants]
  );

  return {
    allGrants,
    assignedGroups,
    assignedUsers,
    roleOptions,
    commitGrants,
    removeGroup,
    removeUser,
    managerConfirmModal,
  };
}
